'use client'
import { useCallback, useEffect, useState, useContext, useRef } from 'react'
import { AchieveTheme, Grid, Paper, Slider } from '@achieve/ascend'
import { Typography, LinkButton } from 'components/Contentful'
import { PhoneDataContext } from 'providers/PhoneDataProvider'
import { AchieveLink } from 'components/AchieveLink'
import { Phone } from 'react-feather'
import { CtaCallButton } from 'components/CtaCallButton'
import { mainProductConfig } from 'constants/mainProductConfig'
import { addUrlParameter } from 'utils/shared'

function SliderToolClient({
  stats,
  ctaText,
  ctaLinkBase,
  slider,
  isLarge,
  sliderStatsTitle,
  sliderQuestion,
  sliderQueryParam,
  title,
  hasPhone,
  phoneWrapper,
  disclosuresLink,
  sectionVariationObject,
  defaultSliderValue = 50000,
}) {
  const [sliderValue, setSliderValue] = useState(defaultSliderValue)
  const [ctaHref, setCtaHref] = useState('')
  const dollarUSLocale = Intl.NumberFormat('en-US')
  const disclosureRef = useRef(null)

  // Cache disclosure target element in a ref once on mount
  useEffect(() => {
    if (disclosuresLink?.linkHref) {
      disclosureRef.current = document.getElementById(disclosuresLink.linkHref)
    }
  }, [disclosuresLink?.linkHref])

  const handleDisclosureClick = useCallback(() => {
    disclosureRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])
  const phoneData = useContext(PhoneDataContext)
  const siteEnv = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT || 'achieve'

  useEffect(() => {
    setCtaHref(addUrlParameter(ctaLinkBase, sliderQueryParam, sliderValue))
  }, [sliderValue, ctaLinkBase, sliderQueryParam])

  function handleChange(e) {
    setSliderValue(e.target.value)
  }

  return (
    <Paper
      className={
        'shadow-none bg-blue-50 relative z-2 mt-[-300px] p-8 rounded-2xl md:mt-[-190px] lg:relative lg:z-1 lg:w-full lg:rounded-md lg:left-0 lg:top-0 xl:mt-12'
      }
      data-testid="product-hero-slider-container"
    >
      <Grid container>
        <Grid size={{ xs: 12 }}>
          <Typography
            content={sliderQuestion}
            variant={isLarge ? 'bodyLg' : 'headingLg'}
            fontWeight="bold"
            className={'text-center mb-4 lg:text-left lg:mb-0'}
            data-testid="product-hero-slider-title"
          />
        </Grid>
        <Grid size={{ xs: 12, xl: 7 }}>
          <div className={'w-full mt-0 mb-6 mx-0 pr-0 lg:mt-2 lg:mb-6 lg:mx-0 lg:pr-8'}>
            <Typography
              content={`$${dollarUSLocale.format(sliderValue)}${
                sliderValue === slider.max && slider.maxPlus ? '+' : ''
              }`}
              variant="displayMd"
              fontWeight="bold"
              className={'text-blue-500 text-center'}
              data-testid="product-hero-dollar-value"
            />
            <Slider
              aria-label="dollar-amount-selection-slider"
              className={'w-full'}
              classes={{
                mark: 'hidden',
              }}
              data-testid="product-hero-slider-input"
              value={sliderValue}
              step={slider.step}
              min={slider.min}
              max={slider.max}
              marks={[
                {
                  value: slider.min,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(slider.min)}`}
                      variant="bodySm"
                      fontWeight="medium"
                      className={'ml-[50px]'}
                      data-testid="product-hero-slider-min"
                    />
                  ),
                },
                {
                  value: slider.max,
                  label: (
                    <Typography
                      content={`$${dollarUSLocale.format(slider.max)}${slider.maxPlus ? '+' : ''}`}
                      variant="bodySm"
                      fontWeight="medium"
                      className={'-ml-20'}
                      data-testid="product-hero-slider-max"
                    />
                  ),
                },
              ]}
              onChange={handleChange}
            />
          </div>
        </Grid>
        <Grid className={'flex items-center justify-center'} size={{ xs: 12, xl: 5 }}>
          <div className={'flex flex-col xl:w-full'}>
            <div>
              <CtaCallButton phoneWrapper={phoneWrapper} variant="blue">
                <LinkButton
                  track={{
                    list_name: 'Hero Form',
                    click_id: ctaText,
                    hero_card_text: title,
                    form_ss_amount: sliderValue,
                    click_position: '0',
                    nav_link_section: 'Hero Form',
                    track_event: 'internal_campaign_click',
                    event_action: 'button_click',
                    event_type: 'button_click',
                    sectionVariationObject,
                  }}
                  content={{
                    linkHref: ctaHref,
                    linkText: ctaText,
                  }}
                  typographicOptions={{ variant: 'bodyLg', fontWeight: 'bold' }}
                  color="primary"
                  variant="contained"
                  className={'min-w-max mt-0 lg:mt-0'}
                  data-testid="product-hero-slider-cta"
                  fullWidth
                />
              </CtaCallButton>
            </div>
            {hasPhone && phoneData.phoneNumber && (
              <AchieveLink
                href={`tel:${phoneData.phoneNumber}`}
                track={{
                  list_name: 'Product Hero Phone',
                  click_id: phoneData.phoneNumber,
                  click_text: `${mainProductConfig[siteEnv || 'achieve']} Web | ${
                    phoneData.phoneNumber
                  }`,
                  click_type: 'Phone Click',
                  nav_link_section: 'Hero',
                  track_event: 'button_click',
                }}
              >
                <div className={'flex flex-row justify-center mt-4'}>
                  <Phone
                    alt="phone button"
                    width={24}
                    height={24}
                    color={AchieveTheme?.ascend?.colors?.blue[500]}
                  />
                  <Typography
                    fontWeight="bold"
                    variant="bodyLg"
                    className={'text-blue-500 ml-2 mr-0 my-0'}
                    content={phoneData.phoneNumber}
                  />
                </div>
              </AchieveLink>
            )}
          </div>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Typography
            content={sliderStatsTitle}
            variant="bodyXs"
            fontWeight="regular"
            className={'mt-6 xl:mt-0'}
            data-testid="product-hero-slider-stats-title"
          />
        </Grid>
        <Grid size={{ xs: 12, xl: 7 }}>
          {stats.map((st) => (
            <div className={'inline-block mr-4 pt-4 lg:mr-10'} key={st.group}>
              <Typography
                content={st.figure}
                variant={isLarge ? 'bodySm' : 'bodyLg'}
                fontWeight="bold"
                data-testid="product-hero-slider-stat-value"
              />
              <Typography
                content={st.group}
                variant="bodyXs"
                fontWeight="regular"
                data-testid="product-hero-slider-stat-desc"
              />
            </div>
          ))}
        </Grid>
        {disclosuresLink && (
          <Grid className={'flex items-end justify-center mt-5'} size={{ xs: 12, xl: 5 }}>
            <AchieveLink
              className={
                'text-neutral-900 underline-offset-[2.4] decoration-0 underline decoration-neutral-900 hover:cursor-pointer'
              }
              onClick={handleDisclosureClick}
            >
              <Typography
                className={
                  'text-neutral-900 underline-offset-[2.4] decoration-0 underline decoration-neutral-900 hover:cursor-pointer'
                }
                content={disclosuresLink?.linkText?.json?.content?.[0]?.content[0]?.value}
                variant="bodySm"
              />
            </AchieveLink>
          </Grid>
        )}
      </Grid>
    </Paper>
  )
}

export { SliderToolClient }
